import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Modal } from "antd";
import makeStyles from '@mui/styles/makeStyles';
import localizationKeys from "../../i18n/localizationKeys";
import PropTypes from "prop-types";

import CloseIcon from "../../../assets/images/custom/close_icon.svg";

import { announceNoteAllEn } from "../../i18n/announceNotes/announceNoteAllEn";
import { announceNoteAllJp } from "../../i18n/announceNotes/announceNoteAllJp";
import { Box } from "@mui/material";
import { borderBottom, boxSizing, margin } from "@mui/system";

const useStyles = makeStyles({
  modal: {
    "&, & *": {
      boxSizing: "border-box",
    },
    backgroundColor: "#161a1e",
    color: "#ffffff",
    paddingBottom: 0,
    top: "60px",
    borderRadius: "8px",

    "& .ant-modal-content": {
      width: "720px",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "center",
      backgroundColor: "#161a1e",
      padding: "40px 20px",
    },
    "& .ant-modal-header": {
      width: "680px",
      height: "98px",
      flexGrow: 0,
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-start",
      alignItems: "center",
      gap: "10px",
      padding: "10px 40px",
      backgroundColor: "unset",
      marginBottom: "0",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      "& .ant-modal-title": {
        width: "100%",
        height: "100%",
        flexGrow: 0,  
        fontSize: "32px",
        fontWeight: "600",
        alignItems: "center",
        fontFamily: "Inter",
        fontStretch: "normal",
        fontStyle: "normal",
        lineHeight: "normal",
        letterSpacing: "normal",
        textAlign: "left",
        color: "#ffffff",
        whiteSpace: "pre-line",
      },
    },
    "& .ant-modal-body": {
      width: "100%",
      display: "flex",
      flexGrow: 0,
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "center",
      padding: "10px 40px",
      paddingBottom: "0px",
      gap: "32px",
    }
  }, 
  btn: {
    boxSizing: "border-box",
    fontSize: "16px",
    flexGrow: 0,
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#28b6c7",
    borderRadius: "5px",
    width: "192px",
    height: "43px",
    cursor: "pointer",
    border: 0,
    padding: "12px 24px",
    bottom: 0,
  },
  btnText: {
    color: "#000",
    width: "144px",
    height: "19px",
    flexGrow: 0,
    whiteSpace: "nowrap",
    fontFamily: "Inter",
    fontSize: "16px",
    fontWeight: "500",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "normal",
    letterSpacing: "normal",
    textAlign: "center",
  },
  infoArea: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: "left",
    flexGrow: 0,
    fontFamily: "Inter",
    fontSize: "16px",
    fontWeight: "normal",
    fontStretch: "normal",
    fontStyle: "normal",
    letterSpacing: "normal",
    marginBottom: "10px",
  },
  noteArea: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    flexGrow: 0,
    fontFamily: "Inter",
    fontSize: "16px",
    fontWeight: "normal",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "normal",
    letterSpacing: "normal",
    textAlign: "left",
    whiteSpace: "pre-line",
    paddingTop: "10px",
    paddingBottom: "10px",
  },
  importantArea: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    flexGrow: 0,
    fontFamily: "Inter",
    fontSize: "16px",
    fontWeight: "normal",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "normal",
    letterSpacing: "normal",
    textAlign: "left",
    whiteSpace: "pre-line",
  },
  paragraph: {
    margin: 0,
  },
  text: {
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  infoTitle: {
    fontStyle: "italic",
    marginRight: "7px",
  },
  importantMessage: {
    fontWeight: 600,
    marginBottom: "8px",
    paddingTop: "10px",
    paddingBottom: "10px",
    textTransform: "uppercase",
  },
});

export function AnnounceNoteModal(props) {
  const { when, onCancel, title, releaseDate, updatedDate, selectedLanguage } = props;
  const classes = useStyles();
  const format = selectedLanguage === "ja" ? "YYYY/MM/DD HH:mm," : "MM/DD/YYYY HH:mm.";
  const releaseDateDisplayed = releaseDate.format(format)
  const updatedDateDisplayed = updatedDate.format(format);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const announceNoteNewest = selectedLanguage === "ja" ? announceNoteAllJp[0] : announceNoteAllEn[0];
  const announceNoteNewestTitle = selectedLanguage === "ja" ? title["ja"] : title["en-US"];
  
  const isInfo = !announceNoteNewestTitle;
  return (
    <Modal
      title={isInfo ? "Information" :  `${announceNoteNewestTitle}`}
      open={when}
      onCancel={onCancel}
      footer={null}
      width={"calc(37.5vw)"}
      height={"fit_contents"}
      closeIcon={<img src={CloseIcon} />}
      maskClosable={true}
      className={classes.modal}
    >
      {<div className={classes.infoArea}>
        <p className={`${classes.text} ${classes.infoTitle}`}>
          {t(localizationKeys.Released_lower)}
        </p>
        <p className={classes.text} style={{ margin: 0, marginRight: "23px"}}>
          {releaseDateDisplayed}
        </p>
        <span className={`${classes.text} ${classes.infoTitle}`}>
          {t(localizationKeys.Updated_lower)}
        </span>
        <span className={classes.text} style={{margin: 0, marginBottom: "0px"}}>
          {updatedDateDisplayed}
        </span>
      </div>}
      <div className={classes.noteArea}>
        <div className={classes.text}>
          {announceNoteNewest[0].introduction.split('\n').map((paragraph, index) => (
            <p key={index} className={classes.paragraph}>
              {paragraph}
            </p>
          ))}
        </div>
      </div>
      <div className={classes.noteArea} style={{paddingTop: "0px", paddingBottom: "0px"}}>
        <div className={classes.text} style={{gap: "0px"}}>
          {announceNoteNewest.slice(1).map((section, idx) => (
            <p key={section.importance} className={classes.importantMessage} >
              {section.importance}
            </ p>
            ))}
        </div>
          <div className={classes.text} style={{paddingTop: "10px", paddingBottom: "10px", gap: "0px"}}>
            {announceNoteNewest.slice(1).map((section, idx) => (
              <p key={section.descriptions} className={classes.paragraph} >
                {section.descriptions}
              </ p>
            ))}
        </div>
      </div>
      <div className={classes.noteArea}>
        <div className={classes.text}>
          {announceNoteNewest[0].conclusion.split('\n').map((paragraph, index) => (
            <p key={index} className={classes.paragraph}>
              {paragraph}
            </p>
          ))}
        </div>
      </div>
      <button
        className={classes.btn}
        onClick={onCancel}
      >
        <div className={classes.btnText}>
          {t(localizationKeys.ConfirmAnnounce_lower)}
        </div>
      </button>
    </Modal>
  );
}

AnnounceNoteModal.propTypes = {
  when: PropTypes.bool,
  title: PropTypes.objectOf(PropTypes.string),
  releaseDate: PropTypes.object,
  updatedDate: PropTypes.object,
  selectedLanguage: PropTypes.string,
  onCancel: PropTypes.func,
}